import { lazy } from 'react'

// LOGIN
const Login = lazy(() => import('../pages/Login'))

// PAGES
const ExploreLands = lazy(() => import('../pages/Explore/DeveloperMap'))
const SaleListings = lazy(() => import('../pages/Explore/SaleListings'))
const RentalListings = lazy(() => import('../pages/Explore/RentalListings'))
const BuildingPermits = lazy(() => import('../pages/Explore/BuildingPermits'))
const Transactions = lazy(() => import('../pages/Explore/Transactions'))
const LandAnalysisList = lazy(
  () => import('../pages/Analysis/LandAnalysisList')
)
const LandAnalysis = lazy(() => import('../pages/Analysis/LandAnalysis'))
const CreateLandAnalysis = lazy(
  () => import('../pages/Analysis/CreateLandAnalysis')
)
const LandGroupingsList = lazy(
  () => import('../pages/Analysis/LandGroupingsList')
)
const GroupLandAnalysis = lazy(
  () => import('../pages/Analysis/GroupLandAnalysis')
)
const CreateLandGrouping = lazy(
  () => import('../pages/Analysis/CreateLandGrouping')
)
const BuildingAnalysisList = lazy(
  () => import('../pages/Analysis/BuildingAnalysisList')
)
const CreateBuildingAnalysis = lazy(
  () => import('../pages/Analysis/CreateBuildingAnalysis')
)
// Temporarily disabled - components need to be migrated to use @popety-io/ui
// const TestPurchaseMarket = lazy(
//   () => import('../pages/Analysis/LandAnalysis/TestPurchaseMarketStandalone')
// )
const BuildingAnalysis = lazy(
  () => import('../pages/Analysis/BuildingAnalysis')
)
const Estimate = lazy(() => import('../pages/Estimate'))
const Modules = lazy(() => import('../pages/Modules'))
const Settings = lazy(() => import('../pages/Settings'))
const PipelineGuard = lazy(() =>
  import('../pages/Pipeline').then((module) => ({
    default: module.PipelineGuard,
  }))
)
const PipelineDetail = lazy(() =>
  import('../pages/Pipeline').then((module) => ({
    default: module.PipelineDetail,
  }))
)
const CreatePipeline = lazy(() => import('../pages/Pipeline/CreatePipeline'))
// const Deal = lazy(() => import('../pages/Pipeline/Deal/Deal'))
const ArchiveDeals = lazy(
  () => import('../pages/Pipeline/ArchiveDeals/ArchiveDeals')
)
const PipelineSettings = lazy(
  () => import('../pages/Pipeline/Settings/PipelineSettings')
)
const PipelineAnalytics = lazy(
  () => import('../pages/Pipeline/Analytics/PipelineAnalytics')
)
const ReportsWelcome = lazy(() =>
  import('../pages/Reports').then((module) => ({
    default: module.ReportsWelcome,
  }))
)
const ReportWizard = lazy(() =>
  import('../pages/Reports').then((module) => ({
    default: module.ReportWizard,
  }))
)
const ReportEditor = lazy(() =>
  import('../pages/Reports').then((module) => ({
    default: module.ReportEditor,
  }))
)
const Alerts = lazy(() => import('../pages/Alerts'))
const TranslationManagement = lazy(
  () => import('../pages/TranslationManagement')
)

/**
 * Role-based access:
 * - Admin: Full access to everything (checked in RouteGuard)
 * - User_landdev: Explore + Analysis
 * - User_brokerinfo: Explore only
 * - User_estimate: Explore + Estimate
 *
 * Routes without allowedRoles are accessible to all authenticated users.
 * Admin role always bypasses allowedRoles check.
 */
const navigations = [
  { path: '/login', page: <Login /> },

  // Explore pages - accessible to all roles (User_landdev, User_brokerinfo, User_estimate)
  { path: '/', private: true, page: <ExploreLands /> },
  { path: '/developer-map', private: true, page: <ExploreLands /> },
  { path: '/sale-listings', private: true, page: <SaleListings /> },
  { path: '/rental-listings', private: true, page: <RentalListings /> },
  { path: '/building-permits', private: true, page: <BuildingPermits /> },
  { path: '/transactions', private: true, page: <Transactions /> },

  // Analysis pages - accessible to all authenticated users
  // Role-based restrictions are handled in create handlers and detail page wrappers

  // Land Analysis - list and create accessible to all, detail requires User_landdev
  { path: '/land-analysis', private: true, page: <LandAnalysisList /> },
  { path: '/land-analysis/new', private: true, page: <CreateLandAnalysis /> },
  {
    path: '/land-analysis/group/:groupingId',
    private: true,
    allowedRoles: ['User_landdev'],
    page: <GroupLandAnalysis />,
  },
  {
    path: '/land-analysis/:id',
    private: true,
    allowedRoles: ['User_landdev'],
    page: <LandAnalysis />,
  },

  // Land Groupings - list and create accessible to all, role check on submit
  { path: '/land-groupings', private: true, page: <LandGroupingsList /> },
  {
    path: '/land-groupings/create',
    private: true,
    page: <CreateLandGrouping />,
  },

  // Building Analysis - list and create accessible to all, detail requires User_landdev
  { path: '/building-analysis', private: true, page: <BuildingAnalysisList /> },
  {
    path: '/building-analysis/new',
    private: true,
    page: <CreateBuildingAnalysis />,
  },
  {
    path: '/building-analysis/:id',
    private: true,
    allowedRoles: ['User_landdev'],
    page: <BuildingAnalysis />,
  },

  // Estimate pages - accessible to all authenticated users
  // Role-based restrictions are handled inside EstimateRouter
  { path: '/estimate/*', private: true, page: <Estimate /> },

  // Pipeline pages - accessible to all authenticated users
  { path: '/pipeline', private: true, page: <PipelineGuard /> },
  { path: '/pipeline/create', private: true, page: <CreatePipeline /> },
  { path: '/pipeline/:id', private: true, page: <PipelineDetail /> },
  {
    path: '/pipeline/:id/deal/:dealId',
    private: true,
    page: <PipelineDetail />,
  },
  {
    path: '/pipeline/:pipelineId/archive',
    private: true,
    page: <ArchiveDeals />,
  },
  {
    path: '/pipeline/:pipelineId/archive/:dealId',
    private: true,
    page: <ArchiveDeals />,
  },
  { path: '/pipeline/:id/settings', private: true, page: <PipelineSettings /> },
  {
    path: '/pipeline/:id/analytics',
    private: true,
    page: <PipelineAnalytics />,
  },

  // Reports pages - accessible to all authenticated users
  { path: '/reports', private: true, page: <ReportsWelcome /> },
  { path: '/reports/create', private: true, page: <ReportWizard /> },
  { path: '/reports/create/:type', private: true, page: <ReportWizard /> },
  {
    path: '/reports/create/:type/:subPath',
    private: true,
    page: <ReportWizard />,
  },
  { path: '/reports/:savedReportId', private: true, page: <ReportEditor /> },

  // Alerts - accessible to all authenticated users
  { path: '/alerts', private: true, page: <Alerts /> },

  // Settings/Modules - accessible to all authenticated users
  { path: '/modules', private: true, page: <Modules /> },
  { path: '/settings', private: true, page: <Settings /> },

  // Translation management - Admin only (handled by allowedRoles check)
  {
    path: '/translation-management',
    private: true,
    page: <TranslationManagement />,
  },

  { path: '*', page: <></> }, // Catch-all route for undefined paths
]

export { navigations }
